import { HomeReview } from '../types';
import { ReviewDetailsOptions } from './reviewCoreApi';
import { normalizeReviewResponses } from './reviewResponseSelector';
import { ReviewTemplateCacheService } from './reviewTemplateCacheService';

export type ReviewReadServiceDependencies = {
  cacheService: any;
  offlineReviewService: any;
  templateCacheService: Pick<ReviewTemplateCacheService, 'ensureReviewTemplate' | 'getTemplateDetails'>;
  onlineApi: {
    getUserReviews(): Promise<HomeReview[]>;
    getReviewDetails(reviewId: number, options?: ReviewDetailsOptions): Promise<HomeReview>;
    getTemplateDetails(templateId: number): Promise<HomeReview['template']>;
    deleteReview(reviewId: number): Promise<void>;
  };
  refreshOnlineState: () => Promise<boolean>;
  getIsOnline: () => boolean;
  useOfflineWorkflow?: () => boolean;
  isWeb: () => boolean;
};

export class ReviewReadService {
  constructor(private readonly deps: ReviewReadServiceDependencies) {}

  async getUserReviews(): Promise<HomeReview[]> {
    const useOfflineWorkflow = !this.deps.isWeb() && this.deps.useOfflineWorkflow?.();
    let offlineReviews: HomeReview[] = [];

    try {
      if (useOfflineWorkflow) {
        await this.ensureOfflineServiceInitialized();

        offlineReviews = await this.getOfflineReviews();
        const isOnline = await this.deps.refreshOnlineState();
        if (!isOnline && offlineReviews.length > 0) {
          return offlineReviews;
        }
      }

      const reviews = await this.deps.onlineApi.getUserReviews();

      if (!Array.isArray(reviews)) {
        console.error('[ReviewService] API did not return an array, got:', typeof reviews, reviews);
        throw new Error('Invalid API response format');
      }

      if (this.deps.isWeb()) {
        reviews.forEach(review => {
          (review as any).reviewData = {
            template: review.template,
            responses: review.responses || [],
            status: review.status,
            submittedAt: review.submittedAt,
            completedAt: review.completedAt,
            property: review.property,
          };
        });
      }

      if (useOfflineWorkflow) {
        await this.cacheReviewsForOffline(reviews);
        return this.mergeReviewLists(offlineReviews, reviews);
      }

      return reviews;
    } catch (error) {
      if (useOfflineWorkflow && offlineReviews.length > 0) {
        console.warn('[ReviewService] Returning cached review list after online refresh failed:', error);
        return offlineReviews;
      }

      console.error('[ReviewService] Error fetching reviews:', error);
      throw error;
    }
  }

  async getReviewDetails(
    reviewId: number,
    options?: ReviewDetailsOptions
  ): Promise<HomeReview> {
    const includeTemplate = options?.includeTemplate ?? true;
    const shouldHydrateTemplate = includeTemplate;

    if (!this.deps.isWeb()) {
      await this.ensureOfflineServiceInitialized();
      let offlineReview: HomeReview | null = null;

      if (this.deps.useOfflineWorkflow?.() && this.deps.offlineReviewService?.getOfflineReview) {
        try {
          offlineReview = await this.deps.offlineReviewService.getOfflineReview(reviewId);

          if (!shouldHydrateTemplate || this.hasTemplateItems(offlineReview)) {
            return offlineReview;
          }
        } catch {
          // Cache miss: fall through to one online fetch. Android reachability can
          // briefly report offline even when API requests will succeed.
        }
      }

      const isOnline = await this.deps.refreshOnlineState();

      if (!isOnline) {
        if (offlineReview) {
          return offlineReview;
        }

        if (!this.deps.useOfflineWorkflow?.()) {
          if (this.deps.offlineReviewService?.getOfflineReview) {
            return this.deps.offlineReviewService.getOfflineReview(reviewId);
          }

          throw new Error('Review details are not available from the API while offline');
        }
      }

      const apiReview = normalizeReviewResponses(
        await this.deps.onlineApi.getReviewDetails(reviewId, options)
      ) as HomeReview;

      if (shouldHydrateTemplate) {
        await this.deps.templateCacheService.ensureReviewTemplate(apiReview);
        await this.ensureUsableTemplate(apiReview);
      }

      if (this.deps.cacheService && this.deps.getIsOnline() && apiReview.propertyId) {
        this.deps.cacheService.refreshRooms(apiReview.propertyId).catch((err: any) => {
          console.error('[ReviewService] ❌ Failed to cache property rooms:', err);
        });
      }

      let reviewToReturn = apiReview;
      if (this.deps.offlineReviewService?.reconcileWithServer) {
        try {
          const reconciliation = await this.deps.offlineReviewService.reconcileWithServer(apiReview);
          if (reconciliation?.needsSync && this.deps.offlineReviewService?.getOfflineReview) {
            reviewToReturn = await this.deps.offlineReviewService.getOfflineReview(reviewId);
            console.warn('[ReviewService] Returning local recovered review after server reconciliation:', {
              reviewId,
              discrepancies: reconciliation.discrepancies?.map((discrepancy: any) => ({
                itemId: discrepancy.itemId,
                type: discrepancy.type,
              })),
            });
          }
        } catch (reconcileError) {
          console.warn('[ReviewService] Failed to reconcile fetched review with offline state:', reconcileError);
        }
      }

      if (this.deps.offlineReviewService?.cacheOnlineReview) {
        const cacheReview = () => this.deps.offlineReviewService.cacheOnlineReview(apiReview);

        if (this.deps.useOfflineWorkflow?.()) {
          try {
            await cacheReview();
          } catch (cacheError) {
            console.error('[ReviewService] Failed to cache fetched review offline:', cacheError);
          }
        } else {
          cacheReview().catch((cacheError: any) => {
            console.error('[ReviewService] Failed to cache fetched review offline:', cacheError);
          });
        }
      }

      return reviewToReturn;
    }

    try {
      const review = normalizeReviewResponses(
        await this.deps.onlineApi.getReviewDetails(reviewId, options)
      ) as HomeReview;

      if (shouldHydrateTemplate) {
        await this.deps.templateCacheService.ensureReviewTemplate(review);
        await this.ensureUsableTemplate(review);
      }

      (review as any).reviewData = {
        template: review.template,
        responses: review.responses || [],
        status: review.status,
        submittedAt: review.submittedAt,
        completedAt: review.completedAt,
        property: review.property,
      };

      return review;
    } catch (error) {
      console.error('[ReviewService] ❌ Error fetching review on web:', error);
      throw error;
    }
  }

  async deleteReview(reviewId: number): Promise<void> {
    if (!this.deps.getIsOnline()) {
      throw new Error('Cannot delete review while offline. Please connect to the internet and try again.');
    }

    return this.deps.onlineApi.deleteReview(reviewId);
  }

  private async ensureOfflineServiceInitialized(): Promise<void> {
    const offlineService = this.deps.offlineReviewService as any;
    if (!offlineService?.initialize || !offlineService?.isServiceInitialized) {
      return;
    }

    if (!offlineService.isServiceInitialized()) {
      await offlineService.initialize();
    }
  }

  private async getOfflineReviews(): Promise<HomeReview[]> {
    if (!this.deps.offlineReviewService?.getOfflineReviews) {
      return [];
    }

    return this.deps.offlineReviewService.getOfflineReviews();
  }

  private async cacheReviewsForOffline(reviews: HomeReview[]): Promise<void> {
    if (!this.deps.offlineReviewService?.cacheOnlineReview) {
      return;
    }

    await Promise.all(
      reviews.map(review =>
        this.deps.offlineReviewService.cacheOnlineReview(review, {
          skipResponseCache: true,
        }).catch((cacheError: any) => {
          console.warn('[ReviewService] Failed to cache review from list offline:', {
            reviewId: review?.id,
            error: cacheError,
          });
        })
      )
    );
  }

  private hasTemplateItems(review: HomeReview): boolean {
    const categories = review.template?.categories;
    if (!Array.isArray(categories) || categories.length === 0) {
      return false;
    }

    const hasItems = (nodes: any[]): boolean =>
      nodes.some(category =>
        (Array.isArray(category?.items) && category.items.length > 0) ||
        (Array.isArray(category?.subcategories) && hasItems(category.subcategories))
      );

    return hasItems(categories);
  }

  private async ensureUsableTemplate(review: HomeReview): Promise<void> {
    const templateId = review.templateId || review.template?.id;
    if (this.hasTemplateItems(review) || !templateId) {
      return;
    }

    try {
      const template = await this.deps.onlineApi.getTemplateDetails(templateId);
      const hydratedReview = {
        ...review,
        template,
      };

      if (!this.hasTemplateItems(hydratedReview)) {
        return;
      }

      review.template = template;

      if ((review as any).reviewData) {
        (review as any).reviewData.template = template;
      }
    } catch (error) {
      console.warn('[ReviewService] Failed to hydrate review template from template details:', {
        reviewId: review.id,
        templateId,
        error,
      });
    }
  }

  private mergeReviewLists(
    offlineReviews: HomeReview[],
    onlineReviews: HomeReview[]
  ): HomeReview[] {
    if (offlineReviews.length === 0) {
      return onlineReviews;
    }

    const merged = [...offlineReviews];
    const localIds = new Set(
      offlineReviews
        .map(review => this.reviewIdKey(review))
        .filter((key): key is string => Boolean(key))
    );
    const localReviewKeys = new Set(
      offlineReviews
        .map(review => this.reviewIdentityKey(review))
        .filter((key): key is string => Boolean(key))
    );

    for (const onlineReview of onlineReviews) {
      const onlineId = this.reviewIdKey(onlineReview);
      const onlineKey = this.reviewIdentityKey(onlineReview);

      if ((onlineId && localIds.has(onlineId)) || (onlineKey && localReviewKeys.has(onlineKey))) {
        continue;
      }

      merged.push(onlineReview);
    }

    return merged;
  }

  private reviewIdKey(review: HomeReview): string | null {
    return review?.id == null ? null : String(review.id);
  }

  private reviewIdentityKey(review: HomeReview): string | null {
    const templateId =
      review.templateId ||
      review.template?.id ||
      (review as any).reviewData?.template?.id;
    const propertyId = review.propertyId || review.property?.id;
    const address = review.property?.address || (review as any).address;

    if (!templateId) {
      return null;
    }

    if (propertyId != null) {
      return `template:${templateId}|property:${propertyId}`;
    }

    if (address) {
      return `template:${templateId}|address:${this.normalizeAddress(String(address))}`;
    }

    return null;
  }

  private normalizeAddress(address: string): string {
    return address
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[.,#]/g, '');
  }
}
